import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchClansDirectory, createClanTechnique, fetchPublicClans } from '../../../services/clans.api';
import { fetchElementsCatalog } from '../../../services/elements.api';
import Modal from '../../../components/Modal/Modal';
import './ClansDirectory.css';

const LIMIT = 12;

const TECH_INITIAL = {
  name: '',
  altName: '',
  // Gestión de tipos como tags
  types: [],
  typesInput: '',
  rank: '',
  element: '',
  action_cost: '',
  price: '',
  damageEffect: '',
  spend: '',
  thresholds_text: '',
  cost_notes: '',
  area: '',
  distance: '',
  duration: '',
  description: '',
  is_signature: false,
  clanIdSelected: ''
};

export default function ClansDirectory() {
  const [page, setPage] = useState(1);
  const [affiliation, setAffiliation] = useState('');
  const [elements, setElements] = useState([]);
  const [sort, setSort] = useState('name asc');

  // Catálogo de elementos desde el backend
  const [elementsCatalog, setElementsCatalog] = useState([]);
  const [elementsCatLoading, setElementsCatLoading] = useState(false);
  const [elementsCatError, setElementsCatError] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ total: 0, page: 1, pages: 1, limit: LIMIT, items: [] });

  // Modal temporal para crear técnica a partir de ejemplo
  const [showCreateTech, setShowCreateTech] = useState(false);
  const [techForm, setTechForm] = useState(TECH_INITIAL);
  const navigate = useNavigate();

  // Lista de clanes para el selector
  const [clansList, setClansList] = useState([]);
  const [clansLoading, setClansLoading] = useState(false);
  const [clansError, setClansError] = useState(null);

  // Cargar catálogo de elementos
  useEffect(() => {
    let cancelled = false;
    async function loadElements() {
      setElementsCatLoading(true);
      setElementsCatError(null);
      try {
        const rows = await fetchElementsCatalog();
        if (!cancelled) setElementsCatalog(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (!cancelled) {
          setElementsCatError(err);
          setElementsCatalog([]);
        }
      } finally {
        if (!cancelled) setElementsCatLoading(false);
      }
    }
    loadElements();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadClans() {
      if (!showCreateTech) return;
      setClansLoading(true);
      setClansError(null);
      try {
        const rows = await fetchPublicClans();
        if (!cancelled) {
          setClansList(Array.isArray(rows) ? rows : []);
        }
      } catch (e) {
        if (!cancelled) setClansError(e);
      } finally {
        if (!cancelled) setClansLoading(false);
      }
    }
    loadClans();
    return () => { cancelled = true; };
  }, [showCreateTech]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchClansDirectory({
          page,
          limit: LIMIT,
          ...(affiliation ? { affiliation } : {}),
          ...(elements.length ? { elements } : {}),
          ...(sort ? { sort } : {})
        });
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [page, affiliation, elements, sort]);

  const onToggleElement = (el) => {
    setPage(1);
    setElements(prev => prev.includes(el) ? prev.filter(x => x !== el) : [...prev, el]);
  };

  const onClear = () => {
    setAffiliation('');
    setElements([]);
    setSort('name asc');
    setPage(1);
  };

  // ====== Helpers para modal temporal de creación de técnica ======
  const openCreateTech = () => {
    setTechForm(TECH_INITIAL);
    setShowCreateTech(true);
  };
  const closeCreateTech = () => setShowCreateTech(false);
  const updateTechField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setTechForm((prev) => ({ ...prev, [field]: value }));
  };
  const buildTechniqueJSON = () => {
    const arr = Array.isArray(techForm.types) ? techForm.types : [];
    const types = arr.map((s) => String(s).trim()).filter(Boolean);
    const obj = {
      name: techForm.name || '',
      altName: techForm.altName || '',
      type: types.join('. '),
      rank: techForm.rank || '',
      action_cost: techForm.action_cost !== '' ? Number(techForm.action_cost) : undefined,
      price: techForm.price !== '' ? Number(techForm.price) : undefined,
      damageEffect: techForm.damageEffect || '',
      spend: techForm.spend || '',
      thresholds_text: techForm.thresholds_text || '',
      element: techForm.element || '',
      cost_notes: techForm.cost_notes || '',
      description: techForm.description || '',
      is_signature: !!techForm.is_signature,
    };
    if (techForm.area) obj.area = techForm.area;
    if (techForm.distance) obj.distance = techForm.distance;
    if (techForm.duration) obj.duration = techForm.duration;
    return JSON.stringify(obj, null, 2);
  };
  const copyTechniqueJSON = async () => {
    try {
      const s = buildTechniqueJSON();
      await navigator.clipboard.writeText(s);
      alert('JSON copiado al portapapeles');
    } catch (err) {
      console.error(err);
      alert('No se pudo copiar. Selecciona y copia manualmente.');
    }
  };
  const loadHarimaExample = () => {
    setTechForm({
      ...TECH_INITIAL,
      name: 'Shukketsu kōto',
      altName: 'Corte sangrante',
      types: ['Kenjutsu', 'Taijutsu'],
      rank: 'D',
      action_cost: 1,
      price: 14,
      damageEffect: '5 + (DES / 3)',
      spend: '4 + (CdC / 6) CK',
      thresholds_text: `DES ≥ 14: Aplica "Herida Abierta" (+1 sangrado si enemigo tiene RES ≤ 12).
CdC ≥ 12: -1 CK de coste.
AGI ≥16: Coste reducido a 0 movimientos (corte rápido).
AGI <8: Coste aumenta a 2 movimientos (falta de agilidad).
Ignora 1 PV de armadura (honor del duelo).
FUE≥14: Sangrado escala con FUE/5 (en lugar de fijo)`,
      description:
        'Corte del Harima que se centra en dejar una herida un tanto abierta para hacer que sangre, centrando el daño en el sangrado.',
    });
    setShowCreateTech(true);
  };

  // Construye payload para el servidor (POST crear técnica)
  const buildTechniquePayloadForServer = () => {
    const arr = Array.isArray(techForm.types) ? techForm.types : [];
    const types = arr.map((s) => String(s).trim()).filter(Boolean);
    const payload = {
      name: techForm.name || '',
      type: types.join('. '),
      rank: techForm.rank || '',
      element: techForm.element || '',
      cost_notes: techForm.cost_notes || null,
      description: techForm.description || null,
      is_signature: !!techForm.is_signature,
    };
    // Identificador de clan (obligatorio)
    if (techForm.clanIdSelected !== '' && Number.isFinite(Number(techForm.clanIdSelected))) {
      payload.clanId = Number(techForm.clanIdSelected);
    }
    // Campos extendidos (no persistidos aún, pero enviados para trazabilidad)
    if (techForm.action_cost !== '') payload.action_cost = Number(techForm.action_cost);
    if (techForm.price !== '') payload.price = Number(techForm.price);
    if (techForm.damageEffect) payload.damageEffect = techForm.damageEffect;
    if (techForm.spend) payload.spend = techForm.spend;
    if (techForm.thresholds_text) payload.thresholds_text = techForm.thresholds_text;
    if (techForm.area) payload.area = techForm.area;
    if (techForm.distance) payload.distance = techForm.distance;
    if (techForm.duration) payload.duration = techForm.duration;
    return payload;
  };

  const createTechInDB = async () => {
    try {
      const payload = buildTechniquePayloadForServer();
      if (payload.clanId == null) {
        alert('Selecciona un Clan para crear la técnica.');
        return;
      }
      await createClanTechnique(payload);
      alert('Técnica creada correctamente.');
      setShowCreateTech(false);
      // Navegar al detalle del clan para verla listada
      navigate(`/clanes/${payload.clanId}`);
    } catch (e) {
      console.error(e);
      alert(`Error creando técnica: ${e?.message || 'desconocido'}`);
    }
  };

  // ====== Gestión de Tipos como etiquetas (tags) ======
  const commitTypesFromString = (input) => {
    const raw = String(input || '');
    // Separadores permitidos: coma, punto, |, ·, &, +
    const parts = raw
      .split(/[,./|·]+|\s*&\s*|\s*\+\s*/g)
      .map((s) => s.trim())
      .filter(Boolean);

    if (parts.length === 0) return { committed: false, remainder: raw };

    setTechForm((prev) => {
      const curr = Array.isArray(prev.types) ? prev.types.slice() : [];
      for (const p of parts) {
        if (!curr.includes(p)) curr.push(p);
      }
      return { ...prev, types: curr };
    });

    return { committed: true, remainder: '' };
  };

  const onRemoveType = (idx) => (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setTechForm((prev) => {
      const curr = Array.isArray(prev.types) ? prev.types.slice() : [];
      if (Number.isInteger(idx) && idx >= 0 && idx < curr.length) {
        curr.splice(idx, 1);
      }
      return { ...prev, types: curr };
    });
  };

  const onTypesInputChange = (e) => {
    const value = e.target.value;
    // Si contiene algún separador, realizar commit parcial
    if (/[,.|·&+]/.test(value)) {
      const { remainder } = commitTypesFromString(value);
      setTechForm((prev) => ({ ...prev, typesInput: remainder }));
    } else {
      setTechForm((prev) => ({ ...prev, typesInput: value }));
    }
  };

  const onTypesKeyDown = (e) => {
    // Enter o Coma -> commit del texto actual
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      if (techForm.typesInput && techForm.typesInput.trim().length) {
        commitTypesFromString(techForm.typesInput);
        setTechForm((prev) => ({ ...prev, typesInput: '' }));
      }
    }
    // Backspace en vacío -> eliminar última etiqueta
    if (e.key === 'Backspace' && (!techForm.typesInput || techForm.typesInput.length === 0)) {
      setTechForm((prev) => {
        const curr = Array.isArray(prev.types) ? prev.types.slice() : [];
        curr.pop();
        return { ...prev, types: curr };
      });
    }
  };

  const canPrev = page > 1;
  const canNext = page < (data.pages || 1);

  return (
    <div className="clan-page clans-directory">
      <header className="clans-directory-hero">
        <div className="clans-directory-hero__overlay" aria-hidden="true" />
        <div className="clans-directory-hero__content">
          <div className="clans-directory-hero__main">
            <div className="clans-directory-hero__emblem" aria-hidden="true">
              氏
            </div>
            <div className="clans-directory-hero__text">
              <span className="clans-directory-hero__eyebrow">氏 · Linajes</span>
              <h1 className="clans-directory-hero__title">Clanes</h1>
              <p className="clans-directory-hero__subtitle">
                Linajes que cruzan aldeas y eras. Cada emblema guarda una herencia; aquí solo se
                muestran los clanes del mundo.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="clans-directory-hero__cta"
            onClick={openCreateTech}
          >
            Nueva técnica (temp)
          </button>
        </div>
        <div className="clans-directory-hero__bottom-line" aria-hidden="true" />
      </header>

      <section className="clan-filters" role="region" aria-label="Filtros de clanes">
        <div className="clan-filters-row clan-inputs-group">
          <label className="clan-field">
            <span className="clan-label">Afiliación</span>
            <input
              className="clan-input"
              type="text"
              placeholder="Ej. Konoha"
              value={affiliation}
              onChange={e => { setAffiliation(e.target.value); setPage(1); }}
            />
          </label>


          <label className="clan-field">
            <span className="clan-label">Ordenar por</span>
            <select
              className="clan-select"
              value={sort}
              onChange={e => { setSort(e.target.value); setPage(1); }}
            >
              <option value="name asc">Nombre (A→Z)</option>
              <option value="name desc">Nombre (Z→A)</option>
              <option value="createdAt desc">Más recientes</option>
              <option value="createdAt asc">Más antiguos</option>
            </select>
          </label>
        </div>

        <div className="clan-filters-row clan-elements">
          <span className="clan-label">Elementos</span>
          {elementsCatalog.map((e) => {
            const code = (e && e.code) ? e.code : e;
            const label = (e && e.displayName) ? e.displayName : code;
            const pressed = elements.includes(code);
            return (
              <label key={code} className="clan-chip" role="button" aria-pressed={pressed}>
                <input
                  type="checkbox"
                  className="visually-hidden"
                  checked={pressed}
                  disabled={elementsCatLoading}
                  onChange={() => onToggleElement(code)}
                />
                <span className="clan-chip-text">{label}</span>
              </label>
            );
          })}
        </div>

        <div className="clan-filters-actions">
          <button type="button" className="clan-btn clan-btn-ghost" onClick={onClear}>Limpiar</button>
        </div>
      </section>

      {loading && (<p className="clan-loading" aria-live="polite">Cargando clanes…</p>)}
      {error && (
        <div className="clan-error" role="alert" aria-live="assertive">
          <p>Error cargando clanes: {error.message || String(error)}</p>
          <button className="clan-btn clan-btn-ghost" onClick={() => setPage(p => p)}>Reintentar</button>
        </div>
      )}

      {!loading && !error && (
        <>
          {(!data.items || data.items.length === 0) ? (
            <div className="clan-empty">
              <h3>No se encontraron clanes</h3>
              <p>Prueba ajustando los filtros o limpiándolos.</p>
            </div>
          ) : (
            <div className="clan-grid">
              {data.items.map(item => {
                const to = item.slug ? `/clanes/${item.slug}` : `/clanes/${item.clanId}`;
                const initial = ((item?.name ?? '?').trim().charAt(0) || '?').toUpperCase();
                return (
                  <Link key={item.clanId} to={to} className="clan-card">
                    <div className="clan-card__header">
                      <div
                        className="clan-card__emblem"
                        aria-hidden={item?.emblemUrl ? undefined : true}
                      >
                        {item?.emblemUrl ? (
                          <img
                            className="clan-card__emblem-img"
                            src={item.emblemUrl}
                            alt=""
                            loading="lazy"
                          />
                        ) : (
                          <span className="clan-card__emblem-fallback">{initial}</span>
                        )}
                      </div>
                      <div className="clan-card__titles">
                        <h3 className="clan-card__name">{item.name}</h3>
                        {item?.affiliation?.name ? (
                          <p className="clan-card__meta">{item.affiliation.name}</p>
                        ) : null}
                      </div>
                    </div>

                    {Array.isArray(item.elements) && item.elements.length > 0 ? (
                      <div className="clan-card__elements">
                        {item.elements.map((el) => (
                          <span key={el} className={`elt elt-${el}`}>
                            {el}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    {item.shortDescription ? (
                      <p className="clan-card__desc">{item.shortDescription}</p>
                    ) : null}

                    <span className="clan-card__more" aria-hidden="true">
                      Ver linaje →
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="clan-pagination">
            <button className="clan-btn clan-btn-ghost" disabled={!canPrev} onClick={() => setPage(p => Math.max(1, p - 1))}>Anterior</button>
            <span className="clan-page-indicator">Página {data.page} de {data.pages || 1}</span>
            <button className="clan-btn clan-btn-ghost" disabled={!canNext} onClick={() => setPage(p => p + 1)}>Siguiente</button>
          </div>
        </>
      )}

      {/* Modal temporal para crear técnica y copiar JSON */}
      <Modal isOpen={showCreateTech} onClose={closeCreateTech} title="Nueva técnica (temp)">
        <form className="clan-modal-form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-row">
            <label>
              <span>Nombre</span>
              <input className="clan-input" type="text" value={techForm.name} onChange={updateTechField('name')} placeholder="Shukketsu kōto" />
            </label>
            <label>
              <span>Nombre alternativo</span>
              <input className="clan-input" type="text" value={techForm.altName} onChange={updateTechField('altName')} placeholder="Corte sangrante" />
            </label>
          </div>

          <div className="form-row">
            <label className="full">
              <span>Clan</span>
              <select
                className="clan-select"
                value={techForm.clanIdSelected}
                onChange={updateTechField('clanIdSelected')}
                disabled={clansLoading}
              >
                <option value="">{clansLoading ? 'Cargando clanes…' : 'Selecciona un clan'}</option>
                {clansError && <option value="" disabled>⚠ Error cargando clanes</option>}
                {clansList.map((c) => (
                  <option key={c.clanId} value={c.clanId}>{c.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label className="full">
              <span>Tipos</span>
              <div className="clan-tag-input">
                <div className="clan-tags">
                  {Array.isArray(techForm.types) && techForm.types.map((t, i) => (
                    <span className="clan-tag" key={`${t}-${i}`}>
                      {t}
                      <button
                        type="button"
                        className="clan-tag-remove"
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onClick={onRemoveType(i)}
                        aria-label={`Quitar ${t}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    className="clan-input clan-tag-editor"
                    type="text"
                    value={techForm.typesInput}
                    onChange={onTypesInputChange}
                    onKeyDown={onTypesKeyDown}
                    placeholder="Escribe y pulsa coma o Enter"
                  />
                </div>
              </div>
            </label>
            <label>
              <span>Rango</span>
              <select
                className="clan-select"
                value={techForm.rank}
                onChange={updateTechField('rank')}
              >
                <option value="">Selecciona rango</option>
                <option value="E">E - Académico</option>
                <option value="D">D - Genin</option>
                <option value="C">C - Chuunin</option>
                <option value="B">B - Jounin</option>
                <option value="A">A - Elite/ANBU</option>
                <option value="S">S - Kage/Sannin</option>
              </select>
            </label>
            <label>
              <span>Elemento</span>
              <select className="clan-select" value={techForm.element} onChange={updateTechField('element')}>
                <option value="">Ninguno</option>
                {elementsCatalog.map((e) => {
                  const code = (e && e.code) ? e.code : e;
                  const label = (e && e.displayName) ? e.displayName : code;
                  return (<option key={code} value={code}>{label}</option>);
                })}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label>
              <span>Coste</span>
              <input className="clan-input" type="number" inputMode="numeric" value={techForm.action_cost} onChange={updateTechField('action_cost')} placeholder="1" />
            </label>
            <label>
              <span>Precio (ryous)</span>
              <input className="clan-input" type="number" inputMode="numeric" value={techForm.price} onChange={updateTechField('price')} placeholder="14" />
            </label>
            <label>
              <span>Daño</span>
              <input className="clan-input" type="text" value={techForm.damageEffect} onChange={updateTechField('damageEffect')} placeholder="5 + (DES / 3)" />
            </label>
          </div>

          <div className="form-row">
            <label className="full">
              <span>Gasto</span>
              <input className="clan-input" type="text" value={techForm.spend} onChange={updateTechField('spend')} placeholder="4 + (CdC / 6) CK" />
            </label>
          </div>

          <div className="form-row">
            <label className="full">
              <span>Notas de coste</span>
              <input className="clan-input" type="text" value={techForm.cost_notes} onChange={updateTechField('cost_notes')} placeholder="Opcional" />
            </label>
          </div>

          <div className="form-row">
            <label>
              <span>Area</span>
              <input className="clan-input" type="text" value={techForm.area} onChange={updateTechField('area')} placeholder="Cono 3m" />
            </label>
            <label>
              <span>Distancia</span>
              <input className="clan-input" type="text" value={techForm.distance} onChange={updateTechField('distance')} placeholder="10m" />
            </label>
            <label>
              <span>Duración</span>
              <input className="clan-input" type="text" value={techForm.duration} onChange={updateTechField('duration')} placeholder="3 turnos" />
            </label>
          </div>

          <div className="form-row">
            <label className="full">
              <span>Umbrales (una regla por línea)</span>
              <textarea className="clan-input" rows="5" value={techForm.thresholds_text} onChange={updateTechField('thresholds_text')} placeholder={`DES ≥ 14: Aplica "Herida Abierta"...`}></textarea>
            </label>
          </div>

          <div className="form-row">
            <label className="full">
              <span>Descripción</span>
              <textarea className="clan-input" rows="4" value={techForm.description} onChange={updateTechField('description')} placeholder="Descripción de la técnica..."></textarea>
            </label>
          </div>

          <div className="form-row">
            <label className="checkbox">
              <input type="checkbox" checked={techForm.is_signature} onChange={updateTechField('is_signature')} />
              <span>Es firma del clan</span>
            </label>
          </div>
        </form>

        <div className="clan-json-preview">
          <h4>JSON generado</h4>
          <textarea readOnly value={buildTechniqueJSON()} />
        </div>

        <div className="clan-modal-actions">
          <button type="button" className="clan-btn clan-btn-ghost" onClick={loadHarimaExample}>Cargar ejemplo Harima</button>
          <button type="button" className="clan-btn clan-btn-ghost" onClick={copyTechniqueJSON}>Copiar JSON</button>
          <button type="button" className="clan-btn clan-btn-ghost" onClick={createTechInDB}>Crear en DB</button>
          <button type="button" className="clan-btn clan-btn-ghost" onClick={closeCreateTech}>Cerrar</button>
        </div>
      </Modal>
    </div>
  );
}